import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";

export const Kpi = ({ id, label, value, sub, warn }: any) => (
  <div className={`bg-card border rounded-xl p-4 ${warn ? "border-amber-300" : ""}`} data-testid={`admin-kpi-${id}`}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-heading text-2xl font-bold mt-1">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export const usd = (n?: number) => `$${(n ?? 0).toFixed(2)}`;

export default function AdminOverview() {
  const [d, setD] = useState<any>(null);
  const [warn, setWarn] = useState<any[]>([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setD(r.data)).catch(() => setErr(true));
    api.get("/admin/orgs", { params: { filter: "approaching" } }).then((r) => setWarn(r.data.orgs)).catch(() => {});
  }, []);
  if (err) return <p className="text-sm text-destructive" role="alert" data-testid="admin-load-error">Couldn’t load the overview. Refresh to try again.</p>;
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const s = d.subscriptions, u = d.month_usage, g = d.google_estimate;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <section>
        <h2 className="font-semibold mb-3">Business</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi id="companies" label="Registered companies" value={d.companies} sub={`${d.new_this_month} new this month · ${d.pilot} pilot`} />
          <Kpi id="active" label="Active subscriptions" value={s.active} sub={`${s.trialing} trial · ${s.past_due} past due · ${s.canceled} canceled · ${d.suspended} suspended`} warn={s.past_due > 0} />
          <Kpi id="mrr-live" label="MRR (live)" value={money(d.mrr.live_cents)} sub="Live-mode Stripe subscriptions only" />
          <Kpi id="mrr-test" label="Test-mode MRR" value={money(d.mrr.test_cents)} sub={`Not revenue · Stripe is in ${d.stripe_mode} mode`} />
        </div>
      </section>
      <section>
        <h2 className="font-semibold mb-3">Activity</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi id="leads" label="Website leads (all-time)" value={d.totals.website_leads} sub={`${u.website_lead} this month`} />
          <Kpi id="estimates" label="Completed estimates" value={d.totals.completed_estimates} sub={`${u.website_estimate_completed} this month`} />
          <Kpi id="field" label="Field Quotes (all-time)" value={d.totals.field_quotes} sub={`${u.field_quote_completed} completed this month`} />
          <Kpi id="email" label={`Emails (${d.month})`} value={d.email.sent || 0}
            sub={`${d.email.failed || 0} failed · ${(d.email.pending || 0) + (d.email.retrying || 0)} pending${d.email.fallback_24h ? ` · ${d.email.fallback_24h} via backup (24h)` : ""}${d.email.branded_active === false ? " · branded sending inactive" : ""}`}
            warn={(d.email.failed || 0) > 0 || (d.email.fallback_24h || 0) > 0 || d.email.branded_active === false} />
        </div>
      </section>
      <section>
        <h2 className="font-semibold mb-3">Google usage ({d.month}, calendar month)</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi id="web-sessions" label="Website map sessions" value={u.website_map_session} />
          <Kpi id="field-sessions" label="Field Quote sessions" value={u.field_map_session} sub={`${u.field_remeasure} re-measures`} />
          <Kpi id="map-loads" label="Map loads (recorded)" value={g.map_loads} sub={`${u.website_map_load} widget · ${u.field_map_load} field · ${u.lead_map_load} lead detail`} />
          <Kpi id="google-cost" label="Estimated Google cost" value={usd(g.total_usd)} warn={d.budget_pct >= 80}
            sub={`${d.budget_pct ?? "—"}% of ${usd(d.pricing.monthly_budget_usd)} budget · ${d.google_billed ? `Google billed ${usd(d.google_billed.amount_usd)}` : "no Google bill entered"}`} />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="admin-instrumented-note">Map loads and address selections are counted by the app in the browser (instrumented), not Google’s billed counts. Autocomplete requests are estimated at {d.pricing.autocomplete_requests_per_select} per selected address. Free tier applied at platform level. Tracking began {d.ledger_started_at ? new Date(d.ledger_started_at).toLocaleDateString() : "—"}; earlier counts are reconstructed from leads ({u.reconstructed} this month).</p>
      </section>
      <section>
        <h2 className="font-semibold mb-3">Approaching allowances</h2>
        {warn.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-approaching-empty">No companies above 80%.</p> : (
          <ul className="space-y-2" data-testid="admin-approaching-list">
            {warn.map((o) => (
              <li key={o.id}><Link to={`/app/admin/companies/${o.id}`} className="flex justify-between bg-card border rounded-lg p-3 text-sm hover:border-primary">
                <span className="font-medium">{o.name}</span><span className="font-mono">{o.allowance.website_used}/{o.allowance.available} · field {o.allowance.field_used}/{o.allowance.field_ceiling}</span>
              </Link></li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
